import type { GetStaticPaths, GetStaticProps, InferGetStaticPropsType } from 'next'
import { type DehydratedState, dehydrate, QueryClient } from '@tanstack/react-query'
import { type FC, useMemo } from 'react'

import { RecipesSinglePageView } from 'views/Recipes'

import { createAllRecipesQuery, createSingleRecipeQuery } from '@segment/api/routes/recipes/queries'

import { createStaticSiteBackend, StaticSiteBackendProvider } from '@segment/contexts/staticSiteBackend'

import type { AppExternalPageProps } from '@segment/next-shared/Providers'

interface RecipesSinglePageProps {
  slug: string
}
const RecipesSinglePage: FC<InferGetStaticPropsType<typeof getStaticProps>> = ({ slug }) => {
  const staticSiteBackend = useMemo(() => createStaticSiteBackend(), [])

  return (
    <StaticSiteBackendProvider value={staticSiteBackend}>
      <RecipesSinglePageView slug={slug} />
    </StaticSiteBackendProvider>
  )
}

export const getStaticPaths: GetStaticPaths = async () => {
  const staticSiteBackend = createStaticSiteBackend()
  const [, queryFn] = await createAllRecipesQuery(staticSiteBackend)
  const recipes = await queryFn()

  return {
    paths: recipes.map(({ slug }) => ({ params: { slug } })),
    fallback: false,
  }
}

export const getStaticProps: GetStaticProps<AppExternalPageProps & RecipesSinglePageProps> = async ({ params }) => {
  const queryClient = new QueryClient()
  const staticSiteBackend = createStaticSiteBackend()

  const slug = String(params?.slug)
  const singleRecipe = await queryClient.fetchQuery(...createSingleRecipeQuery(staticSiteBackend, slug), {
    staleTime: Infinity,
  })

  if (!singleRecipe) {
    throw new Error(`Recipe with slug ${slug} not found`)
  }

  const dangerousDehydratedState = dehydrate(queryClient)

  const safeDehydratedState: DehydratedState = JSON.parse(JSON.stringify(dangerousDehydratedState))

  const props: AppExternalPageProps & RecipesSinglePageProps = {
    dehydratedState: safeDehydratedState,
    slug,
  }

  return {
    props,
  }
}

export default RecipesSinglePage
